import type { MarketOutlookDTO, ValueWithData } from "@eu.mdotm.api/sphere";
import { GraphLegendLabel, GraphLegendTextKey } from "$root/components/GraphLegend";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import DonoutCircle from "$root/components/icons/DonoutCircle";
import { useEventBus } from "$root/event-bus";
import { handlePreviousMarketData } from "$root/functional-areas/market-view/marketUtils";
import { outlookRepository } from "$root/functional-areas/outlook/repository";
import useWidgetsData from "$root/hooks/useWidgetsData";
import { PaletteColors } from "$root/styles/themePalette";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { CircularProgressBar, Icon } from "@mdotm/shadcn/legacy/components";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import OutlookBlockTable, { type TableParams } from "./OutlookBlockTable";

export type CustomSectorModel = {
	label: string;
	type: string;
	prevValue?: ValueWithData;
	currentValue?: ValueWithData;
};

const OutlookBlock: FC = () => {
	const { t } = useTranslation();
	const { currentWidgetsData } = useWidgetsData();
	const dateRange = new Date(Date.parse(currentWidgetsData.creation_time.toString()));
	dateRange.setFullYear(dateRange.getFullYear() - 1);
	dateRange.setHours(0, 0, 0, 0);

	const { data: assetClassTimeSeries } = useMappedQuery(
		(outlookTimeSeries) => {
			return outlookTimeSeries.reduce<{ [key: string]: [number, number, boolean][] }>((acc, el) => {
				const { type, values, inverted } = el;
				if (!type || !values) {
					return acc;
				}

				acc[type] = values
					.filter(([_x, y]) => y ?? false)
					.map(([dateSeconds, y]) => [dateSeconds * 1000, y, inverted]) as [number, number, boolean][];
				return acc;
			}, {});
		},
		[],
		outlookRepository.outlookContinuousTimeSeries.useQuery(dateRange.toISOString().split("T")[0]),
	);

	const composeTableRowData = useCallback(
		(data: Array<CustomSectorModel>) => {
			return data.reduce(
				(acc, { currentValue, prevValue, type }, i) => {
					if (currentValue?.value === undefined) {
						return acc;
					}
					const { previous, tooltip } = handlePreviousMarketData(
						"dashboard",
						(position) => t("DASHBOARD.OUTLOOK", { returnObjects: true })[position],
						{ value: currentValue.value, data: currentValue.data },
						{ value: prevValue?.value, data: prevValue?.data },
					);

					return [
						...acc,
						{
							assetClass: type,
							current: currentValue.value,
							prev: previous,
							tooltip,
							index: i,
						},
					];
				},
				[] as Array<TableParams>,
			);
		},
		[t],
	);

	const sanitaizeData = useCallback(
		({ label, type, currentValue, prevValue }: MarketOutlookDTO) => ({
			label: label ?? "-",
			type: type ?? "-",
			prevValue,
			currentValue,
		}),
		[],
	);

	const outlookQuery = outlookRepository.outlook.useQuery();
	const queryRetrieveOutlookApi = useMappedQuery(
		(outlook) => {
			const refinedData = outlook.reduce<{ [key: string]: CustomSectorModel[] }>((acc, ot) => {
				const { assetClass } = ot;
				if (assetClass) {
					acc[assetClass] = [...(acc[assetClass] ? acc[assetClass] : []), sanitaizeData(ot)];
				}
				return acc;
			}, {});

			return {
				Equity: composeTableRowData(refinedData.EQUITY ?? []),
				FixedIncome: composeTableRowData(refinedData.FIXED_INCOME ?? []),
				Commodities: composeTableRowData(refinedData.COMMODITIES ?? []),
			};
		},
		[composeTableRowData, sanitaizeData],
		outlookQuery,
	);
	const { refetch } = queryRetrieveOutlookApi;

	useEventBus("market-update", () => {
		refetch().catch(noop);
	});

	useWidgetOptions(
		() => ({
			title: t("OUTLOOK.OUTLOOK_TITLE"),
			tooltip: t("OUTLOOK.TOOLTIP"),
			maxHeight: "none",
		}),
		[t],
	);

	return (
		<ReactQueryWrapperBase
			query={queryRetrieveOutlookApi}
			loadingFallback={<CircularProgressBar value="indeterminate" className="m-auto" />}
		>
			{({ Commodities, Equity, FixedIncome }) => (
				// Container-, not viewport-relative: a pinned Sphere AI panel narrows this widget without
				// narrowing the window (see WidgetsGrid.css). The thresholds are the old `lg:`/`xl:` viewport
				// ones minus the chrome the widget never had — the 61px nav rail and the page's 15px gutters.
				<div className="@container flex min-h-0 flex-col gap-6">
					<div className="grid grid-cols-1 @min-[933px]:grid-cols-2 @min-[1189px]:grid-cols-3 gap-6 min-h-0">
						<OutlookBlockTable
							title="DASHBOARD_EQUITY"
							data={Equity ?? []}
							className="h-88!"
							pushTo="/outlook"
							assetClassTimeSeries={assetClassTimeSeries ?? {}}
							sortable
						/>
						<OutlookBlockTable
							title="DASHBOARD_FIXED_INCOME"
							data={FixedIncome ?? []}
							className="h-88!"
							pushTo="/outlook?filter=FI"
							assetClassTimeSeries={assetClassTimeSeries ?? {}}
							sortable
						/>
						<OutlookBlockTable
							title="DASHBOARD_COMMODITIES"
							data={Commodities ?? []}
							className="h-88!"
							pushTo="/outlook?filter=CO"
							assetClassTimeSeries={assetClassTimeSeries ?? {}}
							sortable
						/>
					</div>
					<div className="flex gap-4">
						<div className="flex items-center">
							<Icon className="mr-0!" icon="previous-position" color={PaletteColors.AZURE} size={16} />
							<Icon className="mr-0!" icon="previous-position" color={PaletteColors.NEON_RED} size={16} />
							<Icon className="mr-0!" icon="previous-position" color={PaletteColors.GREEN_BLUE} size={16} />
							<GraphLegendLabel>{t("CURRENT_POSITIONING")}</GraphLegendLabel>
						</div>
						<div className="flex items-center gap-1">
							<DonoutCircle />
							<GraphLegendLabel>{t("PREVIOUS_POSITIONING")}</GraphLegendLabel>
						</div>
						<div className="flex items-center">
							<GraphLegendTextKey>{t("GRAPH_LEGEND.SU_KEY")}</GraphLegendTextKey>
							<GraphLegendLabel>{t("GRAPH_LEGEND.SU_LABEL")}</GraphLegendLabel>
						</div>
						<div className="flex items-center">
							<GraphLegendTextKey>{t("GRAPH_LEGEND.U_KEY")}</GraphLegendTextKey>
							<GraphLegendLabel>{t("GRAPH_LEGEND.U_LABEL")}</GraphLegendLabel>
						</div>
						<div className="flex items-center">
							<GraphLegendTextKey>{t("GRAPH_LEGEND.N_KEY")}</GraphLegendTextKey>
							<GraphLegendLabel>{t("GRAPH_LEGEND.N_LABEL")}</GraphLegendLabel>
						</div>
						<div className="flex items-center">
							<GraphLegendTextKey>{t("GRAPH_LEGEND.O_KEY")}</GraphLegendTextKey>
							<GraphLegendLabel>{t("GRAPH_LEGEND.O_LABEL")}</GraphLegendLabel>
						</div>
						<div className="flex items-center">
							<GraphLegendTextKey>{t("GRAPH_LEGEND.SO_KEY")}</GraphLegendTextKey>
							<GraphLegendLabel>{t("GRAPH_LEGEND.SO_LABEL")}</GraphLegendLabel>
						</div>
					</div>
				</div>
			)}
		</ReactQueryWrapperBase>
	);
};

export default OutlookBlock;
